import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiShoppingCart, FiHeart, FiShare2, FiMinus, FiPlus, FiArrowLeft, FiCheck, FiChevronRight, FiShield } from 'react-icons/fi';
import { Product } from '../types';
import { productApi } from '../services/api';
import { useCart } from '../context/CartContext';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  margin-bottom: 2rem;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.3s ease;

  &:hover {
    background: #f5f5f5;
    color: #1a1a1a;
  }
`;

const ProductContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ImageSection = styled.div`
  max-width: 80vw;
`;

const MainImage = styled.div`
  width: 100%;
  height: 600px;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 1rem;
  position: relative;

  @media (max-width: 768px) {
    height: 400px;
  }
`;

const ProductImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 18px;
`;

const ThumbnailContainer = styled.div`
  display: flex;
  gap: 1rem;
  overflow-x: auto;
  padding: 0.5rem 0;
`;

const Thumbnail = styled.div<{ active: boolean }>`
  width: 80px;
  height: 80px;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid ${props => props.active ? '#1a1a1a' : 'black'};
  transition: all 0.3s ease;
  flex-shrink: 0;

  &:hover {
    border-color: #1a1a1a;
  }
`;

const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ProductInfo = styled.div``;

const BrandName = styled.p`
  color: #666;
  font-size: 16px;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const ProductTitle = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  color: #1a1a1a;
  margin-bottom: 1rem;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const PriceContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const Price = styled.span`
  font-size: 2rem;
  font-weight: bold;
  color: #1a1a1a;
`;

const Currency = styled.span`
  font-size: 1.2rem;
  color: #666;
  margin-left: 4px;
`;

const StockInfo = styled.p<{ inStock: boolean }>`
  color: ${props => props.inStock ? '#27ae60' : '#e74c3c'};
  font-weight: 600;
  margin-bottom: 1.5rem;
`;

const Description = styled.div`
  color: #666;
  line-height: 1.6;
  margin-bottom: 2rem;
`;

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const SizeSection = styled.section`
  padding: 1.25rem;
  border: 1px solid #e3e5eb;
  border-radius: 16px;
  background: #fff;
`;

const SizeHeading = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.8rem;
  strong { font-size: 0.95rem; }
  span { color: #6b7280; font-size: 0.75rem; }
`;

const SizeGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
`;

const SizeButton = styled.button<{ selected: boolean }>`
  min-width: 48px;
  height: 42px;
  padding: 0 0.75rem;
  border: 1px solid ${props => props.selected ? '#5d4bd1' : '#d9dce3'};
  border-radius: 10px;
  background: ${props => props.selected ? '#eeeaff' : '#fff'};
  color: ${props => props.selected ? '#4f3dbf' : '#303744'};
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover { border-color: #7564dd; }
`;

const FashSizeCallout = styled.div`
  margin-top: 0.85rem;
  padding: 1rem;
  border-radius: 13px;
  background: linear-gradient(135deg, #f6f3ff 0%, #eeebff 100%);
  border: 1px solid #ded8ff;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0.9rem;
  align-items: center;
  @media (max-width: 520px) { grid-template-columns: 1fr; }
`;

const FashSizeCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  strong { color: #2d2850; font-size: 0.88rem; }
  span { color: #68627f; font-size: 0.72rem; line-height: 1.4; }
  small { display: flex; align-items: center; gap: 0.3rem; color: #6d619d; font-size: 0.64rem; margin-top: 0.15rem; }
`;

const FashSizeButton = styled.button`
  min-height: 42px;
  padding: 0 0.95rem;
  border: none;
  border-radius: 11px;
  background: linear-gradient(135deg, #6d5ade, #5140bd);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  font-size: 0.75rem;
  font-weight: 800;
  white-space: nowrap;
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(81, 64, 189, 0.2);
  &:hover { transform: translateY(-1px); }
`;

const SizeRecommendation = styled.div`
  margin-top: 0.7rem;
  display: flex;
  align-items: center;
  gap: 0.45rem;
  color: #317057;
  font-size: 0.75rem;
  font-weight: 700;
`;

const SizeError = styled.p`
  margin-top: 0.65rem;
  color: #a23f4c;
  font-size: 0.72rem;
`;

const QuantityContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const QuantityLabel = styled.span`
  font-weight: 600;
  color: #1a1a1a;
`;

const QuantitySelector = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
`;

const QuantityButton = styled.button`
  width: 40px;
  height: 40px;
  border: none;
  background: #f5f5f5;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.3s ease;

  &:hover {
    background: #e0e0e0;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const QuantityInput = styled.input`
  width: 60px;
  height: 40px;
  border: none;
  text-align: center;
  font-weight: 600;
  outline: none;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const AddToCartButton = styled.button`
  flex: 1;
  padding: 1rem 2rem;
  background: #1a1a1a;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover {
    background: #333;
    transform: translateY(-1px);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const ActionButton = styled.button`
  width: 50px;
  height: 50px;
  border: 1px solid #e0e0e0;
  background: white;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    border-color: #1a1a1a;
    background: #f5f5f5;
  }
`;

const FashSnapButton = styled.button`
  flex: 1;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
  }
`;

const FashFastButton = styled.button`
  flex: 1;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, #0cebeb 0%, #20e3b2 50%, #29ffc6 100%);
  color: #1a1a1a;
  border: none;
  border-radius: 8px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-shadow: 0 4px 10px rgba(32, 227, 178, 0.2);

  &:hover {
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 6px 20px rgba(32, 227, 178, 0.4);
  }

  svg {
    filter: drop-shadow(0 0 2px rgba(0,0,0,0.2));
  }
`;

// FashPlay buton container
const FashPlayButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

  // "Üzerimde Gör" - Unified FashPlay Mode
  const FashPlayButton = styled.button`
    width: 100%;
    padding: 0.875rem 1.5rem;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    border-radius: 8px;
    font-weight: 600;
    font-size: 0.9rem;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
    }
  `;



const ProductDetails = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const DetailTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: bold;
  color: #1a1a1a;
  margin-bottom: 1rem;
`;

const DetailList = styled.dl`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 0.5rem 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DetailTerm = styled.dt`
  font-weight: 600;
  color: #1a1a1a;
`;

const DetailDescription = styled.dd`
  color: #666;
  margin: 0;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
  font-size: 1.1rem;
  color: #666;
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #e74c3c;
`;

const ProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [fashSizeMessage, setFashSizeMessage] = useState<string | null>(null);
  const normalizedCategory = product?.category?.toLocaleLowerCase('tr-TR') || '';
  const supportsFashSize = !['aksesuar', 'accessory', 'saat', 'watch', 'ayakkabı', 'shoe'].some(token => normalizedCategory.includes(token));
  const metadataSizes = product?.metaData?.availableSizes || product?.metaData?.sizes;
  const availableSizes = useMemo<string[]>(() => (
    Array.isArray(metadataSizes) && metadataSizes.length
      ? metadataSizes.map(String)
      : ['XS', 'S', 'M', 'L', 'XL', 'XXL']
  ), [metadataSizes]);

  useEffect(() => {
    setSelectedImageIndex(0);
    setQuantity(1);
    setSelectedSize(null);
    setFashSizeMessage(null);
    if (id) {
      fetchProduct(id);
    }
  }, [id]);

  useEffect(() => {
    if (!product) return;
    let attachedSdk: Window['FashionConnect'] | null = null;
    const handleSelection = (selection?: FashionConnectFashSizeSelection) => {
      if (
        !selection ||
        selection.productId !== product.id ||
        !availableSizes.includes(selection.selectedSize)
      ) return;
      setSelectedSize(selection.selectedSize);
      setFashSizeMessage(`${selection.selectedSize} beden avatar profilinize göre seçildi.`);
      window.setTimeout(() => attachedSdk?.close(), 250);
    };
    const attach = () => {
      const sdk = window.FashionConnect;
      if (!sdk || attachedSdk === sdk) return Boolean(attachedSdk);
      sdk.on('fashSizeSelected', handleSelection);
      attachedSdk = sdk;
      return true;
    };
    if (!attach()) {
      const timer = window.setInterval(() => {
        if (attach()) window.clearInterval(timer);
      }, 150);
      return () => {
        window.clearInterval(timer);
        attachedSdk?.off('fashSizeSelected', handleSelection);
      };
    }
    return () => attachedSdk?.off('fashSizeSelected', handleSelection);
  }, [availableSizes, product]);

  const fetchProduct = async (productId: string) => {
    try {
      setLoading(true);
      setError(null);
      const productData = await productApi.getProductById(productId);
      setProduct(productData);
    } catch (err) {
      setError('Ürün bulunamadı');
      console.error('Error fetching product:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (product) {
      addToCart(product, quantity, selectedSize || undefined);
    }
  };

  const handleFashSize = () => {
    if (!product || !window.FashionConnect?.openFashSize) {
      setFashSizeMessage('Fashsize şu anda yüklenemedi. Lütfen sayfayı yenileyin.');
      return;
    }
    setFashSizeMessage(null);
    window.FashionConnect.openFashSize({
      productId: product.id,
      category: product.category,
      productMeta: {
        name: product.title,
        sku: product.sku,
        category: product.category,
        imageUrl: product.images?.[0] || '',
        brandId: product.brandId,
        availableSizes,
      },
    });
  };

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity >= 1) {
      setQuantity(newQuantity);
    }
  };

  const handleFashSnap = () => {
    if (product && (window as any).FashionConnect) {
      (window as any).FashionConnect.openFashSnap({
        productId: product.id,
        productImageUrl: product.images?.[0] || '',
        productTitle: product.title,
        productMeta: {
          sku: product.sku,
          category: product.category,
          price: product.price,
          brand: product.brand?.name,
        },
      });
    } else {
      console.warn('FashionConnect SDK not available');
    }
  };

  /**
   * Unified FashPlay Flow
   * Starts with selfie/body photo upload, then stats, then body type (if needed)
   */
  const handleFashPlay = () => {
    if (product && (window as any).FashionConnect) {
      (window as any).FashionConnect.openFashPlay({
        productId: product.id,
        productImageUrl: product.images?.[0] || '',
        productTitle: product.title,
        productMeta: {
          sku: product.sku,
          category: product.category,
          price: product.price,
          brand: product.brand?.name,
        },
        withSelfie: true, // Always start with selfie flow
      });
    } else {
      console.warn('FashionConnect SDK not available');
    }
  };

  /**
   * FashFast Flow
   * Instant try-on with AI category detection on backend
   */
  const handleFashFast = () => {
    if (product && (window as any).FashionConnect) {
      (window as any).FashionConnect.openFashFast({
        productId: product.id,
        productMeta: {
          name: product.title,
          imageUrl: product.images?.[0] || '',
          price: product.price,
        },
      });
    } else {
      console.warn('FashionConnect SDK not available');
    }
  };



  const isInStock = product && product.stockQuantity && product.stockQuantity > 0;

  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          Ürün yükleniyor...
        </LoadingContainer>
      </Container>
    );
  }

  if (error || !product) {
    return (
      <Container>
        <ErrorContainer>
          <h2>Ürün Bulunamadı</h2>
          <p>{error || 'Aradığınız ürün mevcut değil.'}</p>
          <button onClick={() => navigate('/products')}>
            Ürünlere Geri Dön
          </button>
        </ErrorContainer>
      </Container>
    );
  }

  const currentImage = product.images && product.images.length > 0
    ? product.images[selectedImageIndex]
    : null;

  return (
    <Container>
      <BackButton onClick={() => navigate(-1)}>
        <FiArrowLeft />
        Geri Dön
      </BackButton>

      <ProductContainer>
        <ImageSection>
          <MainImage>
            {currentImage ? (
              <ProductImage src={currentImage} alt={product.title} />
            ) : (
              <ImagePlaceholder>
                Resim Yok
              </ImagePlaceholder>
            )}
          </MainImage>

          {product.images && product.images.length > 1 && (
            <ThumbnailContainer>
              {product.images.map((image, index) => (
                <Thumbnail
                  key={index}
                  active={index === selectedImageIndex}
                  onClick={() => setSelectedImageIndex(index)}
                >
                  <ThumbnailImage src={image} alt={`${product.title} ${index + 1}`} />
                </Thumbnail>
              ))}
            </ThumbnailContainer>
          )}
        </ImageSection>

        <ProductInfo>
          {product.brand && (
            <BrandName>{product.brand.name}</BrandName>
          )}

          <ProductTitle>{product.title}</ProductTitle>

          <PriceContainer>
            <Price>
              {product.price ? product.price.toLocaleString('tr-TR') : 'Fiyat Belirtilmemiş'}
              {product.price && <Currency>{product.currency || 'TRY'}</Currency>}
            </Price>
          </PriceContainer>

          <StockInfo inStock={!!isInStock}>
            {isInStock ? `Stokta ${product.stockQuantity} adet` : 'Stokta Yok'}
          </StockInfo>

          {product.description && (
            <Description>
              <p>{product.description}</p>
            </Description>
          )}

          <ActionsContainer>
            {supportsFashSize && (
              <SizeSection>
                <SizeHeading>
                  <strong>Beden seçin</strong>
                  <span>{selectedSize ? `${selectedSize} seçildi` : 'Bir beden gerekli'}</span>
                </SizeHeading>
                <SizeGrid>
                  {availableSizes.map(size => (
                    <SizeButton
                      type="button"
                      key={size}
                      selected={selectedSize === size}
                      onClick={() => { setSelectedSize(size); setFashSizeMessage(null); }}
                    >
                      {size}
                    </SizeButton>
                  ))}
                </SizeGrid>
                <FashSizeCallout>
                  <FashSizeCopy>
                    <strong>Bedeninizden emin değil misiniz?</strong>
                    <span>Fotoğraf gerektirmeden 3B avatarınızla birkaç dakikada netleştirin.</span>
                    <small><FiShield /> Ölçülerinizi istediğiniz an silebilirsiniz</small>
                  </FashSizeCopy>
                  <FashSizeButton type="button" onClick={handleFashSize}>
                    Fashsize ile bul <FiChevronRight />
                  </FashSizeButton>
                </FashSizeCallout>
                {fashSizeMessage && (
                  selectedSize
                    ? <SizeRecommendation><FiCheck /> {fashSizeMessage}</SizeRecommendation>
                    : <SizeError>{fashSizeMessage}</SizeError>
                )}
              </SizeSection>
            )}
            <QuantityContainer>
              <QuantityLabel>Adet:</QuantityLabel>
              <QuantitySelector>
                <QuantityButton
                  onClick={() => handleQuantityChange(quantity - 1)}
                  disabled={quantity <= 1}
                >
                  <FiMinus />
                </QuantityButton>
                <QuantityInput
                  type="number"
                  value={quantity}
                  onChange={(e) => handleQuantityChange(parseInt(e.target.value) || 1)}
                  min={1}
                />
                <QuantityButton
                  onClick={() => handleQuantityChange(quantity + 1)}
                >
                  <FiPlus />
                </QuantityButton>
              </QuantitySelector>
            </QuantityContainer>

            <ButtonContainer>
              <AddToCartButton
                onClick={handleAddToCart}
                disabled={!isInStock || (supportsFashSize && !selectedSize)}
              >
                <FiShoppingCart />
                Sepete Ekle
              </AddToCartButton>

              <FashSnapButton
                onClick={handleFashSnap}
                title="AI Selfie ile dene"
              >
                ✨ FashSnap
              </FashSnapButton>

              <FashFastButton
                onClick={handleFashFast}
                title="Yapay Zeka ile Hızlı Dene"
              >
                ⚡ FashFast
              </FashFastButton>

              <ActionButton title="Favorilere Ekle">
                <FiHeart />
              </ActionButton>

              <ActionButton title="Paylaş">
                <FiShare2 />
              </ActionButton>
            </ButtonContainer>

            {/* FashPlay Button - Unified Flow */}
            <FashPlayButtonGroup>
              <FashPlayButton
                onClick={handleFashPlay}
                title="Ürünü üzerinizde görün"
              >
                👤 Üzerimde Gör
              </FashPlayButton>
            </FashPlayButtonGroup>
          </ActionsContainer>
        </ProductInfo>
      </ProductContainer>

      <ProductDetails>
        <DetailTitle>Ürün Detayları</DetailTitle>
        <DetailList>
          {product.sku && (
            <>
              <DetailTerm>SKU:</DetailTerm>
              <DetailDescription>{product.sku}</DetailDescription>
            </>
          )}

          <DetailTerm>Kategori:</DetailTerm>
          <DetailDescription>{product.category}</DetailDescription>

          {product.weight && (
            <>
              <DetailTerm>Ağırlık:</DetailTerm>
              <DetailDescription>{product.weight} kg</DetailDescription>
            </>
          )}

          {product.dimensions && (
            <>
              <DetailTerm>Boyutlar:</DetailTerm>
              <DetailDescription>
                {product.dimensions.length} x {product.dimensions.width} x {product.dimensions.height} cm
              </DetailDescription>
            </>
          )}

          {product.tags && product.tags.length > 0 && (
            <>
              <DetailTerm>Etiketler:</DetailTerm>
              <DetailDescription>{product.tags.join(', ')}</DetailDescription>
            </>
          )}
        </DetailList>
      </ProductDetails>
    </Container>
  );
};

export default ProductDetailPage;
